import { companyLandingPageAllowedHosts } from '../constants/Company';
import { PasswordFailureKey, PasswordStrength, passwordMinLength, passwordSpecialCharacterRegex, validPasswordRegex } from '../constants/Password';

class ValidationService {
  validEmailRegex = String.raw`^\S+@\S+\.\S+$`;

  validNameRegex = String.raw`^[^0-9]+$`;

  validPhoneRegex = String.raw`^[(]{0,1}[0-9]{3}[)]{0,1}[-\s\.]{0,1}[0-9]{3}[-\s\.]{0,1}[0-9]{4}$`;

  validMonthRegex = String.raw`^(0?[1-9]|1[012])$`;

  validDayRegex = String.raw`^(0?[1-9]|[12][0-9]|3[01])$`;

  validYearRegex = String.raw`^((19|20)\d{2})$`;

  validZipRegex = String.raw`^[0-9]{5}(?:-[0-9]{4})?$`;

  private static readonly digitRegex = /[0-9]/;

  private static readonly lowerCaseRegex = /[a-z]/;

  private static readonly upperCaseRegex = /[A-Z]/;

  private readonly strengthMeter: PasswordStrength[] = [
    { value: 0, type: 'empty' },
    { value: 1, type: 'weak' },
    { value: 2, type: 'medium' },
    { value: 3, type: 'good' },
    { value: 4, type: 'good' },
    { value: 5, type: 'strong' },
  ];

  public isValidEmail(email: string): boolean {
    const re = new RegExp(this.validEmailRegex);

    return re.test(email);
  }

  public isValidPhone(phone: string): boolean {
    const re = new RegExp(this.validPhoneRegex);

    return re.test(phone);
  }

  public isValidZip(zipCode: string): boolean {
    const re = new RegExp(this.validZipRegex);

    return re.test(zipCode);
  }

  public isAlpha(input?: string): boolean {
    if (!input || typeof input !== 'string') return false;
  
    const re = new RegExp(this.validNameRegex);
    const trimmedInput = input.trim();

    return !!trimmedInput && re.test(trimmedInput);
  }

  public isName(input?: string): boolean {
    if (!input || typeof input !== 'string') return false;

    const trimmedInput = input.trim();

    return !!trimmedInput && this.isAlpha(trimmedInput) && !this.isValidEmail(trimmedInput);
  }

  public isValidUrl(input?: string): boolean {
    if (!input || typeof input !== 'string') return false;

    const trimmedInput = input.trim();

    if (!trimmedInput) return false;

    try {
      const url = new URL(trimmedInput);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  public isValidCompanyLandingPageUrl(input?: string): boolean {
    if (!input || typeof input !== 'string') return false;
  
    const trimmedInput = input.trim();

    if (!trimmedInput) return false;

    try {
      const url = new URL(trimmedInput);
      if (url.protocol !== 'https:') {
        return false;
      }
      const hostname = url.hostname.toLowerCase();
      return companyLandingPageAllowedHosts.includes(hostname);
    } catch {
      return false;
    }
  }

  public isValidPassword(password: string): boolean {
    if (!password || typeof password !== 'string') return false;

    return new RegExp(validPasswordRegex).test(password);
  }

  public passwordFailures(password: string): PasswordFailureKey[] {
    const failures: PasswordFailureKey[] = [];

    if (password.length < passwordMinLength) failures.push('tooShort');
    if (!ValidationService.lowerCaseRegex.test(password)) failures.push('noLower');
    if (!ValidationService.upperCaseRegex.test(password)) failures.push('noUpper');
    if (!ValidationService.digitRegex.test(password)) failures.push('noDigit');
    if (!new RegExp(passwordSpecialCharacterRegex).test(password)) {
      failures.push('noSymbol');
    }

    return failures;
  }

  public passwordStrength(password: string): PasswordStrength {
    if (!password) return this.strengthMeter[0];

    return this.strengthMeter[Math.max(0, this.strengthMeter.length - this.passwordFailures(password).length - 1)];
  }

  public isStrongPassword(password: string): boolean {
    return this.passwordStrength(password).value === 5;
  }
}

export default new ValidationService();
